import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

import {
  combo,
  fractionLatex,
  isValueCorrect,
  scoreParts,
  setField,
  toLatexExpression,
} from "./conditionalProbabilityUtils";

const TYPE = "MaturaConditionalCoinRunOpenTask";

const CONFIGS = [
  { n: 8, k: 2 },
  { n: 9, k: 3 },
  { n: 10, k: 3 },
  { n: 11, k: 3 },
  { n: 12, k: 4 },
];

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeTask(config = rand(CONFIGS)) {
  const conditionCount = combo(config.n, config.k);
  const intersectionCount = config.n - config.k + 1;
  const probability = intersectionCount / conditionCount;

  return {
    type: TYPE,
    points: 3,
    n: config.n,
    k: config.k,
    conditionCount,
    intersectionCount,
    probability,
  };
}

export function generateTask() {
  return makeTask();
}

export function renderTask(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>
      <p>
        Doświadczenie losowe polega na {task.n}-krotnym rzucie symetryczną
        monetą.
      </p>
      <p>
        <strong>
          Oblicz prawdopodobieństwo zdarzenia polegającego na tym, że w tym
          doświadczeniu losowym orzeł wypadnie dokładnie {task.k} razy z rzędu,
          jeśli wiadomo, że orzeł wypadł dokładnie {task.k} razy. Zapisz
          obliczenia.
        </strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p>
          <strong>Krok 1 (1 pkt).</strong> Oblicz moc zdarzenia{" "}
          <InlineMath math="B" />: orzeł wypadł dokładnie {task.k} razy.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="|B| =" />
          <EquationInput
            value={userAnswer.condition || ""}
            onChange={(v) => setField(userAnswer, "condition", v, onChange)}
            disabled={disabled}
            placeholder={`np. C(${task.n},${task.k})`}
            showPreview={true}
            basis="260px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 2 (1 pkt).</strong> Oblicz moc zdarzenia{" "}
          <InlineMath math={"A\\cap B"} />: orzeł wypadł dokładnie {task.k} razy
          i te rzuty tworzą jeden blok kolejnych orłów.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math={"|A\\cap B| ="} />
          <EquationInput
            value={userAnswer.intersection || ""}
            onChange={(v) => setField(userAnswer, "intersection", v, onChange)}
            disabled={disabled}
            placeholder={`np. ${task.n}-${task.k}+1`}
            showPreview={true}
            basis="260px"
          />
        </div>
      </div>

      <div>
        <p>
          <strong>Krok 3 (1 pkt).</strong> Oblicz prawdopodobieństwo warunkowe:
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <InlineMath math="P(A|B) =" />
          <EquationInput
            value={userAnswer.result || ""}
            onChange={(v) => setField(userAnswer, "result", v, onChange)}
            disabled={disabled}
            placeholder="np. 1/15"
            showPreview={true}
            basis="220px"
          />
        </div>
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer = {}) {
  return scoreParts([
    {
      key: "condition",
      label: "moc zdarzenia B",
      correct: isValueCorrect(userAnswer.condition, task.conditionCount),
    },
    {
      key: "intersection",
      label: "moc zdarzenia A∩B",
      correct: isValueCorrect(userAnswer.intersection, task.intersectionCount),
    },
    {
      key: "result",
      label: "prawdopodobieństwo warunkowe",
      correct: isValueCorrect(userAnswer.result, task.probability, 1e-9),
    },
  ]);
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div className="space-y-1">
      <p>
        <InlineMath math={`|B|=${toLatexExpression(userAnswer.condition)}`} />
      </p>
      <p>
        <InlineMath math={`|A\\cap B|=${toLatexExpression(userAnswer.intersection)}`} />
      </p>
      <p>
        <InlineMath math={`P(A|B)=${toLatexExpression(userAnswer.result)}`} />
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong>{" "}
        <InlineMath math={`|B|=\\binom{${task.n}}{${task.k}}=${task.conditionCount}`} />
      </p>
      <p>
        <strong>Krok 2:</strong>{" "}
        <InlineMath math={`|A\\cap B|=${task.n}-${task.k}+1=${task.intersectionCount}`} />
      </p>
      <p>
        <strong>Krok 3:</strong>{" "}
        <InlineMath
          math={`P(A|B)=\\frac{|A\\cap B|}{|B|}=${fractionLatex(
            task.intersectionCount,
            task.conditionCount
          )}`}
        />
      </p>
    </div>
  );
}

export function renderExplanation(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Krok 1.</strong> Oznaczmy przez <InlineMath math="B" /> zdarzenie,
        że orzeł wypadł dokładnie {task.k} razy. Wybieramy miejsca tych orłów
        spośród {task.n} rzutów:
      </p>
      <BlockMath math={`|B|=\\binom{${task.n}}{${task.k}}=${task.conditionCount}`} />

      <p>
        <strong>Krok 2.</strong> Zdarzenie <InlineMath math={"A\\cap B"} /> oznacza,
        że te {task.k} orły wystąpiły jako jeden spójny blok. Taki blok może
        zacząć się na pozycjach od <InlineMath math="1" /> do{" "}
        <InlineMath math={`${task.n}-${task.k}+1`} />.
      </p>
      <BlockMath math={`|A\\cap B|=${task.n}-${task.k}+1=${task.intersectionCount}`} />

      <p>
        <strong>Krok 3.</strong> Korzystamy ze wzoru na prawdopodobieństwo
        warunkowe:
      </p>
      <BlockMath
        math={`P(A|B)=\\frac{|A\\cap B|}{|B|}=\\frac{${task.intersectionCount}}{${task.conditionCount}}=${fractionLatex(
          task.intersectionCount,
          task.conditionCount
        )}`}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
